import { Button, IconButton } from "@material-ui/core";
import {
  CheckCircleOutline,
  DoneOutline,
  Edit,
  Lock,
  MoreVertRounded,
} from "@material-ui/icons";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { REPORT_STATUS, getAge } from "../../../Utils";
import GeneralModal from "../../models/General";
import NoData from "../../NoData";
import api from "../../../services/api";
import { COLORS } from "../../../assets/colors";

const tableHeaders = [
  "Route",
  "Departure",
  "Seats",
  "Fare",
  "Status",
  "Driver",
  "Passengers",
  "Actions",
];

const TableContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  .isActive {
    border: 1px solid #c3c5c9;
  }

  th {
    background-color: #054752;
    padding: 8px;
    color: #fff;
    font-size: 12px;
    font-weight: 200;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${COLORS.adminTextPrimary};
  font-size: 0.875rem;
  vertical-align: middle;
  text-align: left;

  .user-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: inherit;
    
    &:hover .user-name {
      color: ${COLORS.adminAccent};
    }
  }

  .avatar-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .route-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    
    .location {
      font-weight: 600;
      color: ${COLORS.adminTextPrimary};
    }
    .sub-location {
      font-size: 0.75rem;
      color: ${COLORS.adminTextSecondary};
    }
  }

  .departure-info {
    .time {
      font-weight: 600;
      color: ${COLORS.adminTextPrimary};
    }
    .date {
      font-size: 0.75rem;
      color: ${COLORS.adminTextSecondary};
    }
  }

  .passengers-link {
    color: ${COLORS.adminAccent};
    cursor: pointer;
    font-weight: 500;
    font-size: 0.8rem;
    
    &:hover {
      text-decoration: underline;
    }
  }

  .bold {
    font-weight: 600;
  }
`;

const RideTable = ({ rides }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [loadingPassengers, setLoadingPassengers] = useState(false);
  const [passengers, setPassengers] = useState(null);
  const ddref = useRef();

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      setIsOpen(false);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  const handleOutsideClick = (event) => {
    if (ddref.current && !ddref.current.contains(event.target)) {
      setIsOpen(false);
    }
  };
  const toggleDropdown = (item) => {
    setIsOpen(!isOpen);
    setActiveItem(item);
  };
  const togglePassModal = async (item) => {
    setShowPassModal(!showPassModal);
    setActiveItem(item);
    // get passengers if any
    try {
      setLoadingPassengers(true);
      const response = await api.get("/admin/ride/get-passengers", {
        params: {
          ride_id: item.id,
        },
      });

      console.log("response.data", response.data);
      if (response.status == 200) {
        setPassengers(response.data.data);
        setLoadingPassengers(false);
      }
    } catch (e) {
      console.error(e.message);
      setLoadingPassengers(false);
    }
  };
  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <TableRow>
            {tableHeaders.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </TableRow>
        </thead>
        <tbody>
          {rides?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>
                <div className="route-info">
                  <div className="location">
                    {row.leaving_from} &rarr; {row.heading_to}
                  </div>
                  <div className="sub-location">
                    {row.pickup_location} to {row.drop_location}
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <div className="departure-info">
                  <div className="time">
                    {moment(new Date(row.departure_time).getTime()).format("hh:mm a")}
                  </div>
                  <div className="date">{row.departure_date}</div>
                </div>
              </TableCell>

              <TableCell>
                <div className="seats-info">
                  {row.seats_available}/{row.total_seats} Seats
                  {row.front_seats_total != null && (
                    <small
                      style={{
                        display: "block",
                        color: "#64748b",
                        fontSize: "0.75rem",
                        marginTop: "2px",
                      }}
                    >
                      Front: {Math.max(0, row.front_seats_total - (row.front_seats_booked || 0))}/{row.front_seats_total} | Back: {Math.max(0, (row.back_seats_total || (row.total_seats - row.front_seats_total)) - (row.back_seats_booked || 0))}/{row.back_seats_total || (row.total_seats - row.front_seats_total)}
                    </small>
                  )}
                </div>
              </TableCell>

              <TableCell>
                <div className="fare-info">
                  {row.front_seat_price && row.back_seat_price ? (
                    <div>
                      <div>Front: Rs.{row.front_seat_price}</div>
                      <div>Back: Rs.{row.back_seat_price}</div>
                    </div>
                  ) : (
                    `Rs.${row.ticket}`
                  )}
                </div>
              </TableCell>

              <TableCell>
                <StatusBadge status={row.status}>{row.status}</StatusBadge>
              </TableCell>

              <TableCell>
                <Link to={`/profile/${row.User.id}`} className="user-link">
                  <div className="avatar-wrapper">
                    <img
                      src={
                        row.User.avatar_url ||
                        `https://ui-avatars.com/api/?name=${row.User.first_name}&background=random`
                      }
                      alt="avatar"
                    />
                  </div>
                  <span className="user-name bold">{row.User.first_name}</span>
                </Link>
              </TableCell>

              <TableCell>
                <span className="passengers-link" onClick={() => togglePassModal(row)}>
                  View Passengers
                </span>
              </TableCell>

              <TableCell>
                <ActionWrapper>
                  <IconButton onClick={() => toggleDropdown(row)}>
                    <MoreVertRounded />
                  </IconButton>
                  {activeItem?.id === row.id && isOpen && (
                    <DropdownMenu ref={ddref}>
                      <MenuItem onClick={() => setIsOpen(false)}>
                        <CheckCircleOutline fontSize="small" /> Mark Complete
                      </MenuItem>
                      <MenuItem onClick={() => setIsOpen(false)}>
                        <Lock fontSize="small" /> Lock Ride
                      </MenuItem>
                      <MenuItem onClick={() => setIsOpen(false)}>
                        <Edit fontSize="small" /> Edit Ride
                      </MenuItem>
                    </DropdownMenu>
                  )}
                </ActionWrapper>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
      {activeItem?.id && showPassModal && (
        <GeneralModal
          cancelBtnText={"Close"}
          confirmBtnText={"OK"}
          onConfirm={() => setShowPassModal(false)}
          handleClose={() => setShowPassModal(false)}
          open={showPassModal}
          title={"Passengers List"}
          content={
            <div style={{ width: "100%", minWidth: "500px" }}>
              {loadingPassengers && <p>Loading...</p>}
              {!loadingPassengers &&
                passengers?.length > 0 &&
                passengers.map((item) => {
                  return (
                    <Passanger key={item.id}>
                      <img
                        className="avatar-img"
                        src={
                          item?.requestedBy?.avatar_url ||
                          `https://ui-avatars.com/api/?name=${item?.requestedBy.first_name}&background=random`
                        }
                        alt="Passenger"
                      />
                      <div className="info">
                        <div className="passenger-name">
                          {item?.requestedBy?.first_name} {item?.requestedBy?.last_name}
                        </div>
                        <div className="details">
                          <span><strong>Mob:</strong> {item?.requestedBy.mobile_number}</span>
                          <span><strong>Email:</strong> {item?.requestedBy.email}</span>
                          <span><strong>Age:</strong> {getAge(item?.requestedBy.dob)}</span>
                          <span><strong>Gender:</strong> {item?.requestedBy.gender}</span>
                          <span>
                            <strong>Seats:</strong>{" "}
                            {item?.front_seats > 0 && item?.back_seats > 0
                              ? `${item.seats} (${item.front_seats} Front, ${item.back_seats} Back)`
                              : item?.front_seats > 0
                              ? `${item.seats} (Front)`
                              : item?.back_seats > 0
                              ? `${item.seats} (Back)`
                              : item?.seats}
                          </span>
                        </div>
                        <div style={{ marginTop: '0.25rem' }}>
                          <StatusBadge status={item.status === "2" ? "active" : item.status === "1" ? "pending" : "rejected"}>
                            {item.status === "2" ? "Accepted" : item.status === "1" ? "Pending" : "Rejected"}
                          </StatusBadge>
                        </div>
                      </div>
                    </Passanger>
                  );
                })}
            </div>
          }
        />
      )}
    </TableContainer>
  );
};

export default RideTable;
const ActionWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 100;
  min-width: 180px;
  padding: 0.5rem;
`;


const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.6rem 1rem;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.875rem;
  color: ${COLORS.adminTextPrimary};
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: #F1F5F9;
    color: ${COLORS.adminAccent};
  }

  svg {
    color: ${COLORS.adminTextSecondary};
  }
  
  &:hover svg {
    color: ${COLORS.adminAccent};
  }
`;

const Passanger = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.2s ease;
  border-radius: 12px;
  margin-bottom: 0.5rem;

  &:hover {
    background: #F8FAFC;
  }

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .avatar-img {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #fff;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    
    .passenger-name {
      font-size: 1rem;
      font-weight: 700;
      color: ${COLORS.adminTextPrimary};
    }

    .details {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 0.5rem 1rem;
      font-size: 0.8125rem;
      color: ${COLORS.adminTextSecondary};

      span {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        
        strong {
          color: ${COLORS.adminTextPrimary};
          font-weight: 600;
        }
      }
    }
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  text-transform: capitalize;

  background: ${props => {
    switch (String(props.status)?.toLowerCase()) {
      case 'active':
      case '2':
      case 'accepted':
        return 'rgba(16, 185, 129, 0.1)';
      case 'pending':
      case '1':
        return 'rgba(245, 158, 11, 0.1)';
      case 'rejected':
      case '0':
      case 'cancelled':
        return 'rgba(239, 68, 68, 0.1)';
      default:
        return 'rgba(100, 116, 139, 0.1)';
    }
  }};

  color: ${props => {
    switch (String(props.status)?.toLowerCase()) {
      case 'active':
      case '2':
      case 'accepted':
        return COLORS.adminSuccess;
      case 'pending':
      case '1':
        return COLORS.adminWarning;
      case 'rejected':
      case '0':
      case 'cancelled':
        return COLORS.adminError;
      default:
        return COLORS.adminTextSecondary;
    }
  }};
`;
